import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFetchBaseQuery } from "../apiFetchBaseQuery";
import { DefaultApiSuccessResponse } from "@/types/ApiResponse";
import {
	CampaignCsvReportingAgenciesResponse,
	CampaignReviewResponse,
	CsvReportMailTargets,
	SaveCampaignPayload,
	SaveCampaignResponse,
	SaveSpreadsheetRowPayload,
	SaveSpreadsheetRowResponse,
	SpreadsheetResponse,
} from "@/types/CampaignCsvReporting";

export const campaignCsvReportingApiSlice = createApi({
	reducerPath: "campaignCsvReporting",
	baseQuery: apiFetchBaseQuery,
	tagTypes: ["getCsvReportingAgencies", "getCsvReportingSpreadsheet", "getCampaignManageStats"],
	endpoints: builder => ({
		getCsvReportingAgencies: builder.query<CampaignCsvReportingAgenciesResponse, null>({
			query: () => ({
				url: "admin/campaign-csv-export/agencies/nested",
			}),
			providesTags: ["getCsvReportingAgencies"],
		}),
		getCsvReportingSpreadsheet: builder.query<SpreadsheetResponse, null>({
			query: () => ({
				url: "admin/campaign-csv-export/spreadsheet",
			}),
			providesTags: ["getCsvReportingSpreadsheet"],
		}),
		saveSpreadsheetRow: builder.mutation<SaveSpreadsheetRowResponse, { formData: SaveSpreadsheetRowPayload }>({
			query: ({ formData }) => {
				const urlEncodedPayload = new URLSearchParams();

				Object.entries(formData).forEach(([key, value]) => {
					urlEncodedPayload.append(key, String(value));
				});

				return {
					url: "admin/campaign-csv-export/spreadsheet",
					method: "POST",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: urlEncodedPayload.toString(),
				};
			},
		}),
		sendCsvReportEmail: builder.query<
			DefaultApiSuccessResponse,
			{ target: CsvReportMailTargets; idList: string; pdInternalId?: string }
		>({
			query: ({ target, idList, pdInternalId }) => {
				let url = `admin/campaign-csv-export/agencies/${idList}/mail`;
				if (target === "agency") {
					url = `admin/campaign-csv-export/agency/${idList}/mail`;
				} else if (target === "campaign" && pdInternalId) {
					url = `admin/campaign-csv-export/campaign/${pdInternalId}/mail`;
				}

				return {
					url,
				};
			},
		}),
		downloadCsvReport: builder.query<
			Blob,
			{ target: Omit<CsvReportMailTargets, "agencies">; idList: string; pdInternalId?: string }
		>({
			query: ({ target, idList, pdInternalId }) => {
				let url = `admin/campaign-csv-export/agency/${idList}/download`;
				if (target === "campaign" && pdInternalId) {
					url = `admin/campaign-csv-export/campaign/${pdInternalId}/download`;
				}

				return {
					url,
					responseHandler: response => response.blob(),
				};
			},
			providesTags: ["getCsvReportingAgencies"],
		}),
		getCampaignManageStats: builder.query<CampaignReviewResponse, { campaignId: string }>({
			query: ({ campaignId }) => ({
				url: `admin/campaign-csv-export/campaign/${campaignId}/manage`,
			}),
			providesTags: ["getCampaignManageStats"],
		}),
		saveCampaignRow: builder.mutation<SaveCampaignResponse, { formData: SaveCampaignPayload }>({
			query: ({ formData }) => {
				const urlEncodedPayload = new URLSearchParams();

				Object.entries(formData).forEach(([key, value]) => {
					urlEncodedPayload.append(key, String(value));
				});

				return {
					url: `admin/campaign-csv-export/campaign/report/save`,
					method: "PUT",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: urlEncodedPayload.toString(),
				};
			},
		}),
	}),
});

export const {
	useGetCsvReportingAgenciesQuery,
	useGetCsvReportingSpreadsheetQuery,
	useSaveSpreadsheetRowMutation,
	useLazySendCsvReportEmailQuery,
	useLazyDownloadCsvReportQuery,
	useGetCampaignManageStatsQuery,
	useSaveCampaignRowMutation,
} = campaignCsvReportingApiSlice;
